import {BounceLabel} from "@bounce/shared/consumer/constants";
import {ContactMethodType, UserCommunicationPreferences} from "@bounce/shared/consumer/declarations";
import {appendConsent, getIpAddress} from "@bounce/shared/consumer/pages/manage-contact";
import EditContactDialog from "@bounce/shared/consumer/pages/manage-contact/components/EditContactDialog";
import ContactMethodsContent from "@bounce/shared/consumer/pages/manage-contact/ContactMethodsContent";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {merge} from "lodash";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  useAddContactToUser,
  useGetCommunicationPreferencesQuery,
  useUpdateCommunicationPreferences,
} from "~/api/requests";
import {Pages} from "~/bi-events/enum";
import {useAuth} from "@bounce/shared/contexts";
import {useAmplitude} from "~/contexts";

const ContactMethodsDialog = NiceModal.create(() => {
  const {pageViewEvent, pageClickEvent} = useAmplitude();
  const {userId} = useAuth();
  const modal = useModal();

  const closeHandler = (): void => {
    modal.resolve(true);
    modal.hide();
  };
  const [communicationPreferences, setCommunicationPreferences] = useState<UserCommunicationPreferences | undefined>(
    undefined
  );
  const [userIp, setUserIp] = useState("");

  const isValid = useMemo<boolean>(
    () =>
      !!communicationPreferences?.SMS?.allowed ||
      !!communicationPreferences?.email?.allowed ||
      !!communicationPreferences?.phone_call?.allowed,
    [communicationPreferences]
  );

  const getCommunicationPreferencesState = useGetCommunicationPreferencesQuery({});
  const updateCommunicationPreferencesState = useUpdateCommunicationPreferences();
  const useAddPhone = useAddContactToUser();
  const useAddEmail = useAddContactToUser();

  useEffect(() => {
    const result = getCommunicationPreferencesState.data;
    if (result) {
      setCommunicationPreferences(result);
    }
  }, [getCommunicationPreferencesState.data]);

  useEffect(() => {
    getIpAddress().then((ip) => setUserIp(ip));
  }, [setUserIp]);

  const smsChecked = useMemo<boolean>(() => !!communicationPreferences?.SMS?.allowed, [communicationPreferences]);
  const emailChecked = useMemo<boolean>(() => !!communicationPreferences?.email?.allowed, [communicationPreferences]);
  const phoneCallChecked = useMemo<boolean>(
    () => !!communicationPreferences?.phone_call?.allowed,
    [communicationPreferences]
  );
  const whatsappChecked = useMemo<boolean>(
    () => !!communicationPreferences?.whatsapp?.allowed,
    [communicationPreferences]
  );
  const aiCallsChecked = useMemo<boolean>(
    () => !!communicationPreferences?.ai_calls?.allowed,
    [communicationPreferences]
  );
  const voicemailChecked = useMemo<boolean>(
    () => !!communicationPreferences?.voicemail?.allowed,
    [communicationPreferences]
  );

  const smsValue = useMemo<string>(() => communicationPreferences?.SMS?.detail || "", [communicationPreferences]);
  const emailValue = useMemo<string>(() => communicationPreferences?.email?.detail || "", [communicationPreferences]);
  const phoneCallValue = useMemo<string>(
    () => communicationPreferences?.phone_call?.detail || "",
    [communicationPreferences]
  );
  const whatsappValue = useMemo<string>(
    () => communicationPreferences?.whatsapp?.detail || "",
    [communicationPreferences]
  );
  const aiCallsValue = useMemo<string>(
    () => communicationPreferences?.ai_calls?.detail || "",
    [communicationPreferences]
  );
  const voicemailValue = useMemo<string>(
    () => communicationPreferences?.voicemail?.detail || "",
    [communicationPreferences]
  );

  const submitHandler = useCallback(async (): Promise<void> => {
    try {
      const result = await updateCommunicationPreferencesState.mutateAsync({
        ...communicationPreferences!,
      });
      if (result) {
        const consent = appendConsent(smsChecked, emailChecked, phoneCallChecked);
        pageClickEvent({page: Pages.MANAGE_CONTACT, cta: "approve", data: {consent}});
        closeHandler();
      }
    } catch (e) {}
  }, [communicationPreferences, isValid, userIp]);

  const contactMethodCheckedHandler = useCallback(
    (type: ContactMethodType, checked: boolean): void => {
      setCommunicationPreferences((preferences) =>
        preferences ? merge({}, preferences, {[type]: {allowed: checked}}) : undefined
      );
    },
    [setCommunicationPreferences]
  );

  const editValueHandler = useCallback(
    (type: ContactMethodType, value: string): void => {
      if (userId) {
        switch (type) {
          case "SMS":
          case "phone_call":
          case "whatsapp":
          case "ai_calls":
          case "voicemail":
            pageClickEvent({page: Pages.MANAGE_CONTACT, cta: "edit_phone"});
            NiceModal.show(EditContactDialog, {
              type: "tel",
              value: value,
              isLoading: useAddPhone.isLoading,
              onValueUpdated: (value: string) => {
                try {
                  useAddPhone.mutate({
                    contact: {
                      status: "VALID",
                      phone: value,
                      is_primary: true,
                    },
                    contactType: "phone",
                  });
                  if (useAddPhone.isSuccess) {
                    setCommunicationPreferences((preferences) =>
                      preferences ? merge({}, preferences, {[type]: {detail: value}}) : undefined
                    );
                  }
                } catch (e) {}
              },
            });
            break;
          case "email":
            pageClickEvent({page: Pages.MANAGE_CONTACT, cta: "edit_email"});
            NiceModal.show(EditContactDialog, {
              type: "email",
              value: value,
              isLoading: useAddPhone.isLoading,
              onValueUpdated: (value: string) => {
                try {
                  useAddEmail.mutate({
                    contact: {email: value, is_primary: true},
                    contactType: "email",
                  });
                  if (useAddEmail.isSuccess) {
                    setCommunicationPreferences((preferences) =>
                      preferences ? merge({}, preferences, {[type]: {detail: value}}) : undefined
                    );
                  }
                } catch (e) {}
              },
            });
            break;
        }
      }
    },
    [setCommunicationPreferences, phoneCallChecked, smsChecked, userId]
  );

  useEffect(() => {
    pageViewEvent({page: Pages.MANAGE_CONTACT});
  }, []);

  return (
    <ContactMethodsContent
      communicationPreferences={communicationPreferences}
      smsChecked={smsChecked}
      emailChecked={emailChecked}
      phoneCallChecked={phoneCallChecked}
      whatsappChecked={whatsappChecked}
      aiCallsChecked={aiCallsChecked}
      voicemailChecked={voicemailChecked}
      smsValue={smsValue}
      emailValue={emailValue}
      phoneCallValue={phoneCallValue}
      whatsappValue={whatsappValue}
      aiCallsValue={aiCallsValue}
      voicemailValue={voicemailValue}
      submitHandler={submitHandler}
      contactMethodCheckedHandler={contactMethodCheckedHandler}
      editValueHandler={editValueHandler}
      partnerName={BounceLabel}
    />
  );
});

export default ContactMethodsDialog;
